<script lang="ts">
	import type { Mediator, SvelteAsr } from 'types/common'
	import type { WritableDeep } from 'type-fest'
	import type { UserAccount, GroupPermissionMap } from './user.ts'
	import type { PermissionValueMap } from '@isoftdata/svelte-user-configuration'
	import type { UserConfigurationData$result, UserAccount$result, CreateUserAccount$input } from '$houdini'
	import type { i18n } from 'types/common'

	import { klona } from 'klona'
	import { dequal } from 'dequal'
	import { graphql } from '$houdini'
	import session from 'stores/session'
	import toTitleCase from 'to-title-case'
	import { getContext, onMount } from 'svelte'
	import Select from '@isoftdata/svelte-select'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Fieldset from '@isoftdata/svelte-fieldset'
	import { getEventValue } from '@isoftdata/browser-event'
	import { genericPermissionValueMap } from 'utility/permission'
	//TODO: eventually we should move the SaveResetButton component up a level to the configuration state(see Presage's Configuration state for an example)
	import SaveResetButton from '@isoftdata/svelte-save-reset-button'
	import { UserConfiguration, getGroupHighestPermissionValueMap } from '@isoftdata/svelte-user-configuration'

	type UserAccountList = UserConfigurationData$result['userAccounts']

	interface Props {
		asr: SvelteAsr
		userAccounts: UserAccountList
		selectedStoreId: number | null
		hasPermissionToChangePassword?: boolean
		stores: UserConfigurationData$result['stores']
		groups: UserConfigurationData$result['groups']
		permissionValueMap?: PermissionValueMap
		groupPermissionMap?: GroupPermissionMap
		authorizedSitesSet?: Set<number>
		groupMembershipSet?: Set<number>
		wageRates: UserConfigurationData$result['wageRates']
		permissions: UserConfigurationData$result['permissions']
		userAccount: WritableDeep<UserAccount$result['userAccount']>
	}

	let {
		asr,
		userAccounts,
		selectedStoreId,
		hasPermissionToChangePassword = false,
		stores,
		groups,
		permissionValueMap = $bindable(new Map()),
		groupPermissionMap = new Map(),
		authorizedSitesSet = $bindable(new Set<number>()),
		groupMembershipSet = $bindable(new Set<number>()),
		wageRates,
		permissions,
		userAccount = $bindable(),
	}: Props = $props()

	let usernameInput: HTMLInputElement | undefined = $state(undefined)
	let sendPasswordRecoveryToken: boolean = $state(false)
	//Make a frozen deep copy of the mutable data so we can compare it later to see if there are any changes
	let originalData = $state.raw(
		Object.freeze(
			klona({
				userAccount,
				authorizedSitesSet,
				groupMembershipSet,
				permissionValueMap,
			}),
		),
	)

	const { t: translate } = getContext<i18n>('i18next')

	const mediator = getContext<Mediator>('mediator')

	const createUserAccountMutation = graphql(`
		mutation CreateUserAccount($input: NewUserAccount!) {
			createUserAccount(input: $input) {
				...UserAccountFields
			}
		}
	`)

	const updateUserAccountMutation = graphql(`
		mutation UpdateUserAccount($input: UserAccountUpdate!) {
			updateUserAccount(input: $input) {
				...UserAccountFields
			}
		}
	`)

	export function canLeaveState() {
		if (hasUnsavedChanges) {
			return confirm('You have unsaved changes. Are you sure you want to leave? All unsaved changes will be lost.')
		}
		return true
	}

	const absoluteDateTimeFormatter = (date: Date) =>
		new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' }).format(date)
	const sortUserAccountList = (userAccounts: UserAccountList) =>
		userAccounts.slice().sort((a, b) => a.name.localeCompare(b.name))
	function getUserAccountsGroupedByStatus(userAccounts: UserAccountList) {
		const groupedUserAccount = sortUserAccountList(userAccounts).reduce(
			(acc: Record<string, UserAccountList>, userAccount) => {
				const status = toTitleCase(userAccount.status)
				if (!acc[status]) {
					acc[status] = []
				}

				acc[status].push(userAccount)
				return acc
			},
			{},
		)

		// sort groupedUserAccount by status in ascending order
		return Object.keys(groupedUserAccount)
			.sort()
			.reduce(
				(acc, status) => {
					acc[status] = groupedUserAccount[status]
					return acc
				},
				{} as Record<string, UserAccountList>,
			)
	}
	function error({ heading, message }: { heading: string; message: string }) {
		mediator.publish('showMessage', { heading, type: 'danger', time: false, message })
	}

	function success({ heading, message }: { heading: string; message: string }) {
		mediator.publish('showMessage', { heading, type: 'success', time: 3, message })
	}

	function getUserAccountForApi(userAccount: UserAccount): CreateUserAccount$input['input'] {
		return {
			firstName: userAccount.firstName ?? '',
			lastName: userAccount.lastName ?? '',
			name: userAccount.name,
			userGroupIds: Array.from(groupMembershipSet),
			authorizedStoreIds: Array.from(authorizedSitesSet),
			userPermissions: Array.from(permissionValueMap).map(([id, level]) => ({
				id,
				level: genericPermissionValueMap[level],
			})),
			lockNotes: userAccount.lockNotes,
			workEmail: userAccount.workEmail,
			isWorker: userAccount.worker,
			isDriver: userAccount.driver,
			isSalesPerson: userAccount.salesPerson,
			wageRateId: userAccount.wageRate?.id ?? null,
		}
	}

	async function createNewUserAccount(userAccount: UserAccount, hasPermissionToChangePassword: boolean) {
		if (!userAccount.name) {
			throw new Error('You must set a username to create a new user account.')
		}
		if (hasPermissionToChangePassword && !userAccount.newPassword) {
			throw new Error('You must set a password to create a new user account.')
		}

		const res = await createUserAccountMutation.mutate({
			input: {
				...getUserAccountForApi(userAccount),
				newPassword: userAccount.newPassword ?? null,
			},
		})

		return res.data?.createUserAccount.id
	}

	async function updateUserAccount(userAccount: UserAccount, sendPasswordRecoveryToken: boolean) {
		if (userAccount.id) {
			await updateUserAccountMutation.mutate({
				input: {
					...getUserAccountForApi(userAccount),
					id: userAccount.id,
					sendPasswordRecoveryToken,
					status: userAccount.status,
				},
			})
		}
	}

	async function saveChanges() {
		try {
			let asrParams: { lastSavedTime: number | null; lastResetTime: number | null; userAccountId?: number } = {
				lastSavedTime: Date.now(),
				lastResetTime: null,
			}
			if (userAccount.id === null) {
				const userAccountId = await createNewUserAccount(userAccount, hasPermissionToChangePassword)
				asrParams.userAccountId = userAccountId
				asr.go(null, asrParams, { inherit: true })
			} else {
				await updateUserAccount(userAccount, sendPasswordRecoveryToken)
				//Reset the originalData so we don't have unsaved changes
				originalData = Object.freeze(
					klona({
						userAccount,
						authorizedSitesSet,
						groupMembershipSet,
						permissionValueMap,
					}),
				)
			}
		} catch (err) {
			if (err instanceof Error) {
				error({ heading: 'Error', message: err.message })
			} else {
				console.error(err)
			}
		}
	}

	const userAccountsGroupedByStatus = $derived(getUserAccountsGroupedByStatus(userAccounts))
	const userSites = $derived(
		stores.map(({ id, code, name }) => {
			return { id, code, name, isAuthorized: authorizedSitesSet.has(id) }
		}),
	)
	const groupMembership = $derived(
		groups.map(({ groupId, name }) => {
			return { id: groupId, name: name ?? '', isMember: groupMembershipSet.has(groupId) }
		}),
	)
	const groupPermissionValueMap = $derived(getGroupHighestPermissionValueMap(groupMembershipSet, groupPermissionMap))
	const hasUnsavedChanges = $derived(
		!dequal(originalData, {
			userAccount,
			authorizedSitesSet,
			groupMembershipSet,
			permissionValueMap,
		}),
	)
	const accountLimitLicense = $derived($session.licenseInfo.limits.ACTIVE_USER_ACCOUNT_LIMIT)
	const belowLimit = $derived(!!accountLimitLicense?.belowLimit)

	onMount(() => {
		if (userAccount.id === null) {
			usernameInput?.focus()
		}
	})
</script>

<div class="row flex-wrap align-items-end mb-2">
	<div class="col-sm-6 col-md-6 col-lg-3 order-1">
		<Select
			label={translate('common:storeFilter', 'Store Filter')}
			value={selectedStoreId}
			onchange={e => asr.go(null, { storeId: getEventValue(e) }, { inherit: true })}
		>
			{#each stores as store}
				<option value={store.id}>{store.code} - {store.name}</option>
			{/each}
		</Select>
	</div>
	<div class="col-12 col-sm-6 col-md-6 col-lg-4 order-2">
		<Select
			label={translate('common:userAccount', 'User Account')}
			value={userAccount.id}
			emptyText={userAccount.id === null
				? userAccount.name
					? userAccount.name
					: translate('configuration.user.creatingNewAccountInfoHeader', 'New Account')
				: `-- ${translate('configuration.user.selectUserAccount', 'Select User Account')} --`}
			onchange={e => asr.go(null, { userAccountId: getEventValue(e) }, { inherit: true })}
		>
			{#each Object.keys(userAccountsGroupedByStatus) as statusKey}
				<optgroup label={statusKey}>
					{#each userAccountsGroupedByStatus[statusKey] as { id, name, lastLoginDate }}
						<option value={id}
							>{name}{$session.userAccountId === id ? ' (You)' : ''}{lastLoginDate
								? ` - Last Accessed on ${lastLoginDate}`
								: ''}</option
						>
					{/each}
				</optgroup>
			{/each}
		</Select>
	</div>
	<div class="col-12 col-sm-auto mt-2 mt-lg-0 mb-1 order-3">
		{#if userAccount.id !== null}
			<Button
				outline
				size="sm"
				color="success"
				iconClass="plus"
				disabled={!belowLimit}
				onclick={() => {
					asr.go(null, { userAccountId: null }, { inherit: true })
				}}>{translate('configuration.user.creatingNewAccountInfoHeader', 'New Account')}</Button
			>
		{/if}
	</div>
	<div class="mb-1 ms-md-auto order-0 order-md-4">
		<SaveResetButton
			disabled={!hasUnsavedChanges}
			resetHref={asr.makePath(null, { lastResetTime: Date.now(), lastSaveTime: null }, { inherit: true })}
			save={() => saveChanges()}
		/>
	</div>
</div>

<UserConfiguration
	{userSites}
	{groupMembership}
	{permissions}
	{permissionValueMap}
	{groupPermissionValueMap}
	{hasPermissionToChangePassword}
	{error}
	{success}
	siteLabel="Store"
	canToggleActive={(userAccount.status !== 'DEACTIVATED' && userAccount.status !== 'LOCKED') || belowLimit}
	bind:userAccount
	bind:usernameInput
	bind:doSendPasswordRecoveryToken={sendPasswordRecoveryToken}
	siteAccessChange={async site => {
		site.isAuthorized ? authorizedSitesSet.add(site.id) : authorizedSitesSet.delete(site.id)
		authorizedSitesSet = new Set(authorizedSitesSet)
	}}
	groupMembershipChange={async ({ id, isMember }) => {
		//The user can't adjust the group permissions in this UI, and we've got the permissions for each group in a Map in memory, so we just need to update their groupMembershipSet here
		isMember ? groupMembershipSet.add(id) : groupMembershipSet.delete(id)
		//Just doing mySet = mySet doesn't seem to trigger a reactivity update, so we need to create a new Set. Ugh.
		//This was the only workaround I could find that worked. I also tried using SvelteSet, and writing groupMembershipSet to a tmp var and then assigning that tmp to groupMembershipSet, but neither worked.
		//But also I tried to reproduce this in a Svelte 5 playground and could not, so I'm not sure what's going on here.
		groupMembershipSet = new Set(groupMembershipSet)
	}}
	permissionValueChange={async ({ permissionIds, value }) => {
		for (const id of permissionIds) {
			permissionValueMap.set(id, value)
		}
		permissionValueMap = new Map(permissionValueMap)
	}}
>
	{#snippet userAccountInfo()}
		<Select
			labelClass="pt-1"
			label={translate('configuration.user.wageRate', 'Wage Rate')}
			value={userAccount.wageRate?.id ?? null}
			onchange={e => {
				const value = getEventValue(e)
				if (value) {
					userAccount.wageRate = {
						id: parseInt(value, 10),
					}
				} else {
					userAccount.wageRate = null
				}
			}}
		>
			{#each wageRates as { id, name }}
				<option value={id}>{name}</option>
			{/each}
		</Select>
		<Fieldset
			class="mt-3"
			label={translate('configuration.user.roles', 'Roles')}
		>
			<div class="ps-2 d-flex flex-wrap flex-column">
				<div>
					<Checkbox
						label={translate('common:salesperson', 'Salesperson')}
						bind:checked={userAccount.salesPerson}
					/>
				</div>
				<div>
					<Checkbox
						label={translate('configuration.user.deliveryDriver', 'Delivery Driver')}
						bind:checked={userAccount.driver}
					/>
				</div>
				<div>
					<Checkbox
						label={translate('configuration.user.workOrderMechanic', 'Work Order Mechanic')}
						bind:checked={userAccount.worker}
					/>
				</div>
			</div>
		</Fieldset>
	{/snippet}
</UserConfiguration>
